import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Image as ImageIcon, Upload, X, Save } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const HomepageImagesEditor = () => {
  const [settings, setSettings] = useState({
    hero_background_image: '',
    hero_background_overlay: 'true', // Dark overlay for text readability
    hero_background_opacity: '0.5',
    hero_background_position: 'center', // CSS background-position
    about_section_image_left: '',
    about_section_image_right: '',
    cta_section_image_left: '',
    cta_section_image_right: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({
    hero: false,
    aboutLeft: false,
    aboutRight: false,
    ctaLeft: false,
    ctaRight: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const keys = [
        'hero_background_image',
        'hero_background_overlay',
        'hero_background_opacity',
        'hero_background_position',
        'about_section_image_left',
        'about_section_image_right',
        'cta_section_image_left',
        'cta_section_image_right',
      ];

      const responses = await Promise.all(
        keys.map(key =>
          axios.get(`${API_URL}/business-info/${key}`, config)
            .catch(() => ({ data: { info_value: '' } }))
        )
      );

      const newSettings = {};
      keys.forEach((key, index) => {
        const defaultValue = key === 'hero_background_overlay' ? 'true' 
                           : key === 'hero_background_opacity' ? '0.5'
                           : key === 'hero_background_position' ? 'center'
                           : '';
        newSettings[key] = responses[index].data.info_value || defaultValue;
      });

      setSettings(newSettings);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load homepage image settings:', error);
      toast.error('Failed to load settings');
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    setUploading(prev => ({ ...prev, [type]: true }));

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = sessionStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const imageUrl = response.data.url;
      let settingKey = '';
      
      if (type === 'hero') settingKey = 'hero_background_image';
      else if (type === 'aboutLeft') settingKey = 'about_section_image_left';
      else if (type === 'aboutRight') settingKey = 'about_section_image_right';
      else if (type === 'ctaLeft') settingKey = 'cta_section_image_left';
      else if (type === 'ctaRight') settingKey = 'cta_section_image_right';
      
      setSettings(prev => ({
        ...prev,
        [settingKey]: imageUrl,
      }));

      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleRemoveImage = (type) => {
    let settingKey = '';
    if (type === 'hero') settingKey = 'hero_background_image';
    else if (type === 'aboutLeft') settingKey = 'about_section_image_left';
    else if (type === 'aboutRight') settingKey = 'about_section_image_right';
    else if (type === 'ctaLeft') settingKey = 'cta_section_image_left';
    else if (type === 'ctaRight') settingKey = 'cta_section_image_right';
    
    setSettings(prev => ({
      ...prev,
      [settingKey]: '',
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };

      await Promise.all(
        Object.entries(settings).map(([key, value]) =>
          axios.put(`${API_URL}/business-info/${key}`, { value }, config)
        )
      );

      toast.success('Homepage image settings saved successfully');
      window.dispatchEvent(new Event('homepageImagesUpdated'));
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const renderImageUpload = (type, label, description) => {
    let imageUrl = '';
    let isUploading = false;
    
    if (type === 'hero') {
      imageUrl = settings.hero_background_image;
      isUploading = uploading.hero;
    } else if (type === 'aboutLeft') {
      imageUrl = settings.about_section_image_left;
      isUploading = uploading.aboutLeft;
    } else if (type === 'aboutRight') {
      imageUrl = settings.about_section_image_right;
      isUploading = uploading.aboutRight;
    } else if (type === 'ctaLeft') {
      imageUrl = settings.cta_section_image_left;
      isUploading = uploading.ctaLeft;
    } else if (type === 'ctaRight') {
      imageUrl = settings.cta_section_image_right;
      isUploading = uploading.ctaRight;
    }

    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold mb-2">{label}</h3>
        <p className="text-sm text-gray-600 mb-3">{description}</p>
        
        {imageUrl ? (
          <div className="relative">
            <img
              src={imageUrl}
              alt={label}
              className="w-full h-48 object-cover rounded-lg border border-gray-300"
            />
            <button
              onClick={() => handleRemoveImage(type)}
              className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title="Remove image"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files[0], type)}
              className="hidden"
              id={`upload-${type}`}
              disabled={isUploading}
            />
            <label
              htmlFor={`upload-${type}`}
              className="cursor-pointer flex flex-col items-center"
            >
              {isUploading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              ) : (
                <>
                  <Upload className="text-gray-400 mb-2" size={32} />
                  <span className="text-sm text-gray-600">Click to upload image</span>
                  <span className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</span>
                </>
              )}
            </label>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <ImageIcon className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Homepage Images</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Customize the images displayed on your homepage to create a unique and engaging experience for your visitors.
      </p>

      {/* Hero Section */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Hero Section (Top Banner)</h3>
        
        {renderImageUpload(
          'hero',
          'Hero Background Image',
          'Large background image for the top section of your homepage. Recommended size: 1920x1080px or larger.'
        )}

        {settings.hero_background_image && (
          <div className="mt-4 space-y-4">
            {/* Overlay Toggle */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.hero_background_overlay === 'true'}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    hero_background_overlay: e.target.checked ? 'true' : 'false' 
                  }))}
                  className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium">Add dark overlay for better text readability</span>
              </label>
            </div>

            {/* Opacity Slider */}
            {settings.hero_background_overlay === 'true' && (
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overlay Darkness: {Math.round(parseFloat(settings.hero_background_opacity) * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.hero_background_opacity}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    hero_background_opacity: e.target.value 
                  }))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Adjust how dark the overlay appears over your background image
                </p>
              </div>
            )}

            {/* Background Position - Draggable */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Position - Click and drag to reposition
              </label>
              <div 
                className="relative w-full h-48 border-2 border-gray-300 rounded-lg overflow-hidden cursor-move bg-gray-100"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  setSettings(prev => ({ 
                    ...prev, 
                    hero_background_position: `${x.toFixed(1)}% ${y.toFixed(1)}%`
                  }));
                }}
                onMouseMove={(e) => {
                  if (e.buttons === 1) { // Left mouse button is pressed
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                    setSettings(prev => ({ 
                      ...prev, 
                      hero_background_position: `${x.toFixed(1)}% ${y.toFixed(1)}%`
                    }));
                  }
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${settings.hero_background_image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: settings.hero_background_position || 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
                {/* Crosshair indicator */}
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-white rounded-full shadow-lg flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Current position: <span className="font-mono font-semibold">{settings.hero_background_position || 'center'}</span>
              </p>
              <p className="text-xs text-gray-500">
                Click and drag anywhere on the preview to adjust the focal point
              </p>
            </div>
          </div>
        )}
      </div>

      {/* About Section */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">About Section Images</h3>
        <p className="text-sm text-gray-600 mb-4">You can add images on the left, right, or both sides of the services section.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderImageUpload(
            'aboutLeft',
            'Left Side Image',
            'Image displayed on the left. Recommended size: 800x600px.'
          )}
          
          {renderImageUpload(
            'aboutRight',
            'Right Side Image',
            'Image displayed on the right. Recommended size: 800x600px.'
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Call-to-Action Section Images</h3>
        <p className="text-sm text-gray-600 mb-4">You can add images on the left, right, or both sides of the booking section.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {renderImageUpload(
            'ctaLeft',
            'Left Side Image',
            'Image displayed on the left. Recommended size: 800x600px.'
          )}
          
          {renderImageUpload(
            'ctaRight',
            'Right Side Image',
            'Image displayed on the right. Recommended size: 800x600px.'
          )}
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSaveSettings}
        disabled={saving}
        className="btn btn-primary flex items-center space-x-2"
      >
        <Save size={18} />
        <span>{saving ? 'Saving...' : 'Save Homepage Images'}</span>
      </button>
    </div>
  );
};

export default HomepageImagesEditor;
